import { useEffect, useState } from 'react';

import { fetchStock, fetchTickerIndex } from '../lib/api.js';

/**
 * Load the precomputed analysis for one ticker.
 *
 * In-flight requests are aborted when the selection changes, so fast typing in
 * the search bar can never paint stale results over a newer one.
 *
 * @param {string | null} ticker bare NSE symbol
 */
export function useStock(ticker) {
  const [state, setState] = useState({ status: 'idle', data: null, error: null });

  useEffect(() => {
    if (!ticker) {
      setState({ status: 'idle', data: null, error: null });
      return undefined;
    }

    const controller = new AbortController();
    setState({ status: 'loading', data: null, error: null });

    fetchStock(ticker, { signal: controller.signal })
      .then((data) => setState({ status: 'ready', data, error: null }))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState({ status: 'error', data: null, error: error.message });
      });

    return () => controller.abort();
  }, [ticker]);

  return state;
}

/**
 * Load the searchable universe index once and keep it in memory.
 */
export function useTickerIndex() {
  const [state, setState] = useState({ status: 'loading', data: [], error: null });

  useEffect(() => {
    const controller = new AbortController();

    fetchTickerIndex(controller.signal)
      .then((data) => setState({ status: 'ready', data, error: null }))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        setState({ status: 'error', data: [], error: error.message });
      });

    return () => controller.abort();
  }, []);

  return state;
}
